import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { Container, BtnBack, Fonts, Button, LabelBox } from 'components/base-ui'
import ScrollPage from 'components/base-ui/ScrollPage'
import { getSrcSetStr } from 'helpers/image'
import { HandleApplyPromoCode, HandleRemoveCartAdjustment } from 'types'
import { CustomerAdjustmentInvalidDataType } from 'types'
import { useTranslation } from 'react-i18next'
import { displayPrice } from 'helpers'
import { getInvalidDatas, invalidDataEnum } from 'helpers/promotion'
import { AdjustmentTargetEntityTypeEnum } from 'graphQL/dinein/generate/operations'
import {
  AdjustmentSourceEnumType,
  AdjustmentInvalidReasonEnumType,
  AdjustmentTypeEnumType,
  ExternalCouponRewardTypeEnum,
} from 'graphQL/hatostore/generate/operations'

enum TagTypeEnum {
  success = 'success',
  warning = 'warning',
  error = 'error',
}

type TagTypeEnumStr = keyof typeof TagTypeEnum

interface TagType {
  type: TagTypeEnumStr
  text: string
}

export interface CartAdjustment {
  promotionID: number
  externalCouponID: number
  memberCouponID: number
}

export interface CouponType {
  id: number
  rewardType: ExternalCouponRewardTypeEnum | AdjustmentTypeEnumType
  thumbnailImageURL: string
  serialNumber: string
  isAvailable: boolean
  name: string
  endTime: string
  source?: AdjustmentSourceEnumType
  memberCouponRefID: string
  description: string
}

interface PromotionDetailsProps {
  data: CouponType | null
  onBack: () => void
  cartAdjustments: CartAdjustment[]
  handleApplyPromoCode: HandleApplyPromoCode
  handleRemoveCartAdjustment: HandleRemoveCartAdjustment
  targetEntity?: keyof typeof AdjustmentTargetEntityTypeEnum
  haveMemberCondition: boolean
  isAutoPromotion?: boolean
  invalidReason?: AdjustmentInvalidReasonEnumType
  invalidData?: CustomerAdjustmentInvalidDataType
}

const PromotionDetails = ({
  data,
  onBack,
  cartAdjustments,
  handleRemoveCartAdjustment,
  handleApplyPromoCode,
  targetEntity,
  haveMemberCondition,
  isAutoPromotion,
  invalidReason,
  invalidData,
}: PromotionDetailsProps) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const scrollPageRef = useRef(null)

  const invaliddDatas = getInvalidDatas(invalidData)

  let iconImage = ''
  let isAppliedPromotion = false
  let currentAdjustment
  let externalCouponID
  let hatoHeartMemberCouponID
  let promotionID
  let isExpired = false
  if (data) {
    switch (targetEntity) {
      case AdjustmentTargetEntityTypeEnum.ShippingFee:
        iconImage = haveMemberCondition
          ? '/assets/generic/promotions/icon-promotion-member-shippingfee.png'
          : '/assets/generic/promotions/icon-promotion-general-shippingfee.png'
        break
      default:
        iconImage = haveMemberCondition
          ? '/assets/generic/promotions/icon-promotion-member-discount.png'
          : '/assets/generic/promotions/icon-promotion-general-discount.png'
    }

    if (data.rewardType === AdjustmentTypeEnumType.Cash) {
      iconImage = '/assets/generic/promotions/icon-promotion-cash-voucher.png'
    }

    if (data.thumbnailImageURL) {
      iconImage = data.thumbnailImageURL
    }

    if (data.source === AdjustmentSourceEnumType.Promotion) {
      currentAdjustment = cartAdjustments.find((adjustment) => adjustment.promotionID === data.id)
    } else if (data.source === AdjustmentSourceEnumType.MemberCoupon) {
      currentAdjustment = cartAdjustments.find((adjustment) => adjustment.memberCouponID === data.id)
    } else {
      currentAdjustment = cartAdjustments.find((adjustment) => adjustment.externalCouponID === data.id)
    }

    if (currentAdjustment) {
      isAppliedPromotion = true
      externalCouponID = currentAdjustment.externalCouponID
      promotionID = currentAdjustment.promotionID
      hatoHeartMemberCouponID = currentAdjustment.memberCouponID
    }

    isExpired = moment(data.endTime, 'YYYY-MM-DD').isBefore(moment())
  }

  const tags: TagType[] = []
  if (isAutoPromotion || isAppliedPromotion) {
    if (!invalidReason) {
      tags.push({ type: TagTypeEnum.success, text: t('promotionUsedSwitch') + '!' })
    }
    if (invalidReason === AdjustmentInvalidReasonEnumType.InvalidConditions && invaliddDatas.length === 0) {
      tags.push({ type: TagTypeEnum.warning, text: t('invalidCondition') })
    } else if (
      invalidReason === AdjustmentInvalidReasonEnumType.CodeHasBeenUsed ||
      invalidReason === AdjustmentInvalidReasonEnumType.CouponHasBeenUsed
    ) {
      tags.push({ type: TagTypeEnum.warning, text: t('invalidConditionUsed') })
    }
  }

  if (data?.endTime) {
    tags.push({
      type: isExpired ? TagTypeEnum.error : TagTypeEnum.success,
      text: `${t('expire')}: ${moment(data.endTime).format('D/M/YY')}`,
    })
  }

  return (
    <PromotionDetailsContainer id="promotion-detail-page">
      <ScrollPage ref={scrollPageRef}>
        {data && (
          <PageContentWrapper>
            <PromotionCover>
              <BtnBackWrapper>
                <Container>
                  <BtnBack enableFastClick={false} onClick={onBack} />
                </Container>
              </BtnBackWrapper>
            </PromotionCover>

            <PromotionContent>
              <BoxIconPromotionType>
                {iconImage === data.thumbnailImageURL ? (
                  <ImageIconCoupon alt="promotion-icon-coupon" src={iconImage} />
                ) : (
                  <ImageIconCoupon
                    alt="promotion-icon-coupon"
                    src={`${process.env.PUBLIC_URL}${iconImage}`}
                    srcSet={getSrcSetStr({
                      img: `${process.env.PUBLIC_URL}${iconImage}`,
                      hires2x: true,
                      hires3x: true,
                    })}
                  />
                )}
              </BoxIconPromotionType>
              <Container>
                {tags.length > 0 && (
                  <TagWrapper>
                    {tags.map((tag, i) => (
                      <Tag key={i} mode={tag.type}>
                        <Fonts.SmallTitleBold>{tag.text}</Fonts.SmallTitleBold>
                      </Tag>
                    ))}
                  </TagWrapper>
                )}
                {invalidData && invaliddDatas.indexOf(invalidDataEnum.remainingMinimumSatangs) !== -1 && (
                  <InvalidMessage>
                    <LabelBox
                      title={t('minimumPricePromotionTitle')}
                      text={t('minimumPricePromotionDes', {
                        price: displayPrice(invalidData.remainingMinimumSatangs / 100),
                      })}
                      type="warning"
                    />
                  </InvalidMessage>
                )}
                <PromotionTitle id="promotion-name">{data.name}</PromotionTitle>
                {data.description && (
                  <SectionDetails id="promotion-description">
                    <Fonts.Medium>{t('productDetailDetail')}</Fonts.Medium>
                    <Fonts.BaseTitleRegular>{data.description}</Fonts.BaseTitleRegular>
                  </SectionDetails>
                )}
              </Container>
            </PromotionContent>

            {!isAutoPromotion ? (
              <>
                {(data.isAvailable || isAppliedPromotion) && (
                  <ButtonWrapperFooter>
                    {isAppliedPromotion ? (
                      <Button
                        id="btn-cancel-promotion"
                        md2
                        error
                        onClick={removeCouponFromCart}
                        isProcessing={isLoading}
                        disabled={isLoading}
                        enableFastClick={false}
                      >
                        {t('btnRemovePromotionDetails', {
                          rewardType:
                            data.rewardType === AdjustmentTypeEnumType.Discount
                              ? t('tabMenuCoupon')
                              : t('tabMenuVoucher'),
                        })}
                      </Button>
                    ) : (
                      <Button
                        id="btn-apply-promotion"
                        md2
                        isProcessing={isLoading}
                        disabled={isLoading}
                        onClick={handleApplyCouponToCart}
                        enableFastClick={false}
                      >
                        {t('btnApplyPromotionDetails', {
                          rewardType:
                            data.rewardType === AdjustmentTypeEnumType.Discount
                              ? t('tabMenuCoupon')
                              : t('tabMenuVoucher'),
                        })}
                      </Button>
                    )}
                  </ButtonWrapperFooter>
                )}
              </>
            ) : (
              <div></div>
            )}
          </PageContentWrapper>
        )}
      </ScrollPage>
    </PromotionDetailsContainer>
  )

  function applyCouponToCart() {
    setIsLoading(true)

    if (data) {
      const { source, serialNumber, id } = data

      const isPromotion = source === AdjustmentSourceEnumType.Promotion && serialNumber
      const isExternalCoupon =
        source === AdjustmentSourceEnumType.ExternalCoupon || source === AdjustmentSourceEnumType.ExternalMemberCoupon
      const isMemberCoupon = source === AdjustmentSourceEnumType.MemberCoupon

      const callbacks = {
        onCompleted: () => {
          setIsLoading(false)
          onBack()
        },
        onFailed: (promotionInvalids) => {
          setIsLoading(false)
        },
      }

      const promoCodeParams = { id, ...callbacks }

      if (isPromotion || isExternalCoupon) {
        handleApplyPromoCode({ ...promoCodeParams, promoCode: serialNumber })
      } else if (isMemberCoupon) {
        handleApplyPromoCode({ ...promoCodeParams, hatoHeartMemberCouponID: id })
      } else {
        handleApplyPromoCode(promoCodeParams)
      }
    }
  }

  function handleApplyCouponToCart() {
    applyCouponToCart()
  }

  function removeCouponFromCart() {
    if (
      (externalCouponID === undefined || externalCouponID === null) &&
      (promotionID === undefined || promotionID === null) &&
      (hatoHeartMemberCouponID === undefined || hatoHeartMemberCouponID === null)
    ) {
      return false
    }

    setIsLoading(true)

    handleRemoveCartAdjustment({
      externalCouponID,
      promotionID: hatoHeartMemberCouponID || externalCouponID ? undefined : promotionID,
      hatoHeartMemberCouponID,
      onCompleted: () => {
        setIsLoading(false)
        onBack()
      },
      onFailed: (promotionInvalids) => {
        setIsLoading(false)
      },
    })
  }
}

export default PromotionDetails

const PromotionDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`

const PageContentWrapper = styled.div`
  padding-bottom: 100px;

  .iphoneX & {
    padding-bottom: 120px;
  }
`

const BtnBackWrapper = styled.div`
  position: relative;
`

const PromotionCover = styled.div`
  position: relative;
  padding-top: 17px;
  height: 100px;
  background-color: #f7f7f7;
`

const PromotionContent = styled.div`
  position: relative;
  margin-top: -22px;
  background-color: white;
  flex: 1;
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
  padding-top: 44px;
`

const BoxIconPromotionType = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 72px;
  height: 72px;
  border-radius: 8px;
  background-color: white;
  right: 24px;
  top: 0;
  transform: translate(0, -50%);
`

const ImageIconCoupon = styled.img`
  position: absolute;
  width: auto;
  height: 52px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const PromotionTitle = styled(Fonts.H2)`
  margin-bottom: 8px;
`

const SectionDetails = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 10px;
  }

  ${Fonts.Medium} {
    margin-bottom: 5px;
  }
`

const ButtonWrapperFooter = styled.div`
  display: flex;
  justify-content: center;
  position: fixed;
  bottom: 30px;
  left: 0;
  right: 0;

  .iphoneX & {
    padding-bottom: 50px;
  }
`

const TagWrapper = styled.div`
  margin-top: -30px;
  margin-bottom: 4px;
`

const Tag = styled.div<{ mode: TagTypeEnumStr }>`
  margin-right: 5px;
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 15px;

  &:last-child {
    margin-right: 0px;
  }

  ${(p) =>
    p.mode === TagTypeEnum.success &&
    `
    background-color: ${p.theme.colors.successLight};
    color: ${p.theme.colors.successDark};
  `}

  ${(p) =>
    p.mode === TagTypeEnum.warning &&
    `
    background-color: ${p.theme.colors.warningLightWhite};
    color: ${p.theme.colors.warningDark};
  `}

  ${(p) =>
    p.mode === TagTypeEnum.error &&
    `
    background-color: ${p.theme.colors.errorLightWhite};
    color: ${p.theme.colors.errorLight};
  `}
`

const InvalidMessage = styled.div`
  margin-bottom: 24px;
`
